import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { FIELD_TYPE_OPTIONS } from '@/types/docflow';
import type { IOutputField, FieldType } from '@/types/docflow';

interface FieldSchemaEditorProps {
  fields: IOutputField[];
  onChange: (fields: IOutputField[]) => void;
}

const FieldSchemaEditor: React.FC<FieldSchemaEditorProps> = ({ fields, onChange }) => {
  const { t } = useTranslation('docflow');

  const addField = useCallback(() => {
    const newField: IOutputField = {
      key: `field_${fields.length + 1}`,
      label: `Field ${fields.length + 1}`,
      type: 'string',
      required: false,
    };
    onChange([...fields, newField]);
  }, [fields, onChange]);

  const updateField = useCallback((index: number, updates: Partial<IOutputField>) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  }, [fields, onChange]);

  const removeField = useCallback((index: number) => {
    onChange(fields.filter((_, i) => i !== index));
  }, [fields, onChange]);

  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;
    const reordered = Array.from(fields);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    onChange(reordered);
  }, [fields, onChange]);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('config.fields')}
        </label>
        <button
          type="button"
          onClick={addField}
          className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
        >
          + {t('config.addField')}
        </button>
      </div>

      {fields.length === 0 ? (
        <div className="text-sm text-gray-400 dark:text-gray-500 italic py-4 text-center border border-dashed border-gray-300 dark:border-gray-600 rounded">
          No fields defined. Click "Add Field" to start.
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="fields">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                {/* Header */}
                <div className="grid gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 px-1" style={{ gridTemplateColumns: '1.25rem 1fr 1fr 4rem 1.25rem 1.25rem' }}>
                  <div></div>
                  <div>{t('config.fieldKey')}</div>
                  <div>{t('config.fieldLabel')}</div>
                  <div>{t('config.fieldType')}</div>
                  <div>{t('config.fieldRequired')}</div>
                  <div></div>
                </div>

                {fields.map((field, index) => (
                  <Draggable key={`field-${index}`} draggableId={`field-${index}`} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="grid gap-1 items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-1 py-1"
                        style={{ ...provided.draggableProps.style, gridTemplateColumns: '1.25rem 1fr 1fr 4rem 1.25rem 1.25rem' }}
                      >
                        <div className="flex justify-center" {...provided.dragHandleProps}>
                          <svg className="w-4 h-4 text-gray-400 cursor-grab" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                          </svg>
                        </div>
                        <div>
                          <input
                            type="text"
                            value={field.key}
                            onChange={(e) => updateField(index, { key: e.target.value })}
                            className="w-full text-xs px-1.5 py-1 border border-gray-200 dark:border-gray-600 rounded bg-transparent dark:text-gray-200"
                          />
                        </div>
                        <div>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => updateField(index, { label: e.target.value })}
                            className="w-full text-xs px-1.5 py-1 border border-gray-200 dark:border-gray-600 rounded bg-transparent dark:text-gray-200"
                          />
                        </div>
                        <div>
                          <select
                            value={field.type}
                            onChange={(e) => updateField(index, { type: e.target.value as FieldType })}
                            className="w-full text-xs px-1 py-1 border border-gray-200 dark:border-gray-600 rounded bg-transparent dark:text-gray-200"
                          >
                            {FIELD_TYPE_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex justify-center">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => updateField(index, { required: e.target.checked })}
                            className="w-4 h-4"
                          />
                        </div>
                        <div className="flex justify-center">
                          <button
                            type="button"
                            onClick={() => removeField(index)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};

export default FieldSchemaEditor;
